import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import { login } from '../api';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { VOHKLogo } from '../components/VOHKLogo';
import { toast } from 'sonner';
import { Loader2, User, Lock } from 'lucide-react';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginUser } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await login(username, password);
      loginUser(response.data.token, response.data.user);
      toast.success(`Welcome, ${response.data.user.username}!`);
      navigate('/dashboard');
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gradient-to-br from-maroon-50 to-white">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <VOHKLogo size="3xl" variant="maroon" className="mx-auto mb-4" />
            <h1 className="text-4xl font-bold tracking-tight text-maroon-950">
              VOHK
            </h1>
            <p className="mt-2 text-sm text-maroon-700">
              Textile Inventory Management
            </p>
          </div>

          <form onSubmit={handleLogin} className="mt-8 space-y-6" data-testid="login-form">
            <div className="space-y-4">
              <div>
                <Label htmlFor="username" className="text-maroon-800">
                  Username
                </Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-maroon-400" />
                  <Input
                    id="username"
                    data-testid="username-input"
                    type="text"
                    placeholder="Enter username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    className="pl-10 h-11 border-maroon-300 focus:border-maroon-500 focus:ring-maroon-500"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-maroon-800">
                  Password
                </Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-maroon-400" />
                  <Input
                    id="password"
                    data-testid="password-input"
                    type="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10 h-11 border-maroon-300 focus:border-maroon-500 focus:ring-maroon-500"
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-11 bg-maroon-900 hover:bg-maroon-800 text-white font-medium transition-colors duration-200"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>

          <div className="text-center text-xs text-maroon-500 mt-6 space-y-1">
            <p className="font-medium">Available Users:</p>
            <p>admin / jay / shruti / jagruti</p>
          </div>
        </div>
      </div>

      {/* Right side - Background with maroon gradient */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-maroon-900 via-maroon-800 to-maroon-950 relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <VOHKLogo height={160} variant="white" className="mx-auto mb-6" />
            <h2 className="text-5xl font-bold mb-4">Welcome to VOHK</h2>
            <p className="text-maroon-200 text-lg">Manage your textile orders efficiently</p>
          </div>
        </div>
      </div>
    </div>
  );
}
